import { z } from 'zod';

/** 40% gross margin on service sub-labor: price = cost ÷ (1 − 0.40). */
export const SUB_LABOR_TARGET_GROSS_MARGIN = 0.4;

const round2 = (n: number): number => Math.round(n * 100) / 100;
const ceil2 = (n: number): number => Math.ceil(n * 100) / 100;

export interface SubTimeBillingInput {
  /** Real worked hours, often unknown (subs rarely report). Portal-only. */
  workedHours?: number;
  travelHours?: number;
  customerRate: number;
  subCost: number;
  /** Gross-margin fraction in [0,1). Defaults to SUB_LABOR_TARGET_GROSS_MARGIN. */
  grossMargin?: number;
}

export interface SubTimeBilling {
  /** Real worked + travel hours. Portal-only — NOT what we post to CW. */
  realHours: number;
  /** Cost-covering hours posted to CW as actualHours (CW ignores hoursBilled). */
  billedHours: number;
  /** Revenue floor that covers sub cost + margin: subCost ÷ (1 − grossMargin). */
  floor: number;
  /** billedHours × customerRate — always ≥ floor. */
  invoiceAmount: number;
}

/**
 * Reconcile #488's goals: cost-covering CW billing + a portal-only record of
 * the sub's real hours. realHours (worked + travel) never leaves the portal;
 * billedHours is the cost-covering figure posted to CW as actualHours.
 */
export function computeSubTimeBilling(input: SubTimeBillingInput): SubTimeBilling {
  const grossMargin = input.grossMargin ?? SUB_LABOR_TARGET_GROSS_MARGIN;
  if (!(input.customerRate > 0)) {
    throw new Error('customerRate must be greater than 0.');
  }
  if (!(grossMargin >= 0 && grossMargin < 1)) {
    throw new Error('grossMargin must be in [0, 1).');
  }
  if (input.subCost < 0) {
    throw new Error('subCost must be >= 0.');
  }
  const realHours = round2((input.workedHours ?? 0) + (input.travelHours ?? 0));
  const floor = input.subCost / (1 - grossMargin);
  const requiredBilled = ceil2(floor / input.customerRate);
  const billedHours = Math.max(realHours, requiredBilled);
  // Round UP to the cent (not nearest) — billedHours * customerRate is always
  // ≥ floor by construction, but rounding to nearest cent can land a hair
  // below floor (e.g. customerRate=137.33, billedHours=5.56 → 763.55 <
  // 763.554663…). ceil2 preserves the invoiceAmount ≥ floor guarantee.
  const invoiceAmount = ceil2(billedHours * input.customerRate);
  return { realHours, billedHours, floor, invoiceAmount };
}

export const subWorkedTimeInputSchema = z.object({
  ticketType: z.enum(['ServiceTicket', 'ProjectTicket']),
  vendorName: z.string().min(1),
  subCompanyId: z.number().int().positive().optional(),
  workedHours: z.number().positive().optional(),
  travelHours: z.number().min(0).optional(),
  workDate: z.string().min(1), // ISO date; route converts to Date
  memberId: z.number().int().positive(),
  customerRate: z.number().positive(),
  subCost: z.number().min(0),
  subLaborId: z.string().optional(),
  note: z.string().optional(),
}).strip(); // drop unknown keys (e.g. a client-supplied `actor`)

export type SubWorkedTimeInput = z.infer<typeof subWorkedTimeInputSchema>;

/** Real time reported by an assigned subcontractor; never creates a CW entry. */
export const subSelfReportedTimeInputSchema = z.object({
  workedHours: z.number().positive().max(24),
  travelHours: z.number().min(0).max(24).default(0),
  workDate: z.string()
    .regex(/^\d{4}-\d{2}-\d{2}$/, 'workDate must be YYYY-MM-DD')
    .refine((value) => {
      const parsed = new Date(`${value}T00:00:00Z`);
      return !Number.isNaN(parsed.getTime()) && parsed.toISOString().slice(0, 10) === value;
    }, 'workDate must be a real calendar date'),
  note: z.string().trim().max(2000).optional(),
}).refine((value) => value.workedHours + value.travelHours <= 24, {
  message: 'workedHours plus travelHours cannot exceed 24',
  path: ['workedHours'],
});

export type SubSelfReportedTimeInput = z.infer<typeof subSelfReportedTimeInputSchema>;
